import React, { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { $Employees, $EmployeesStates, fetchEmployees } from "@store/company/employees-store";
import { getCompanyLogo } from "@utils/getCompanyLogo";

import { CompanyTabPropsT, CompanyContactT } from "@interfaces/company/company";
import CompanyService from "@services/company-service";
import Dropzone from "@ui/dropzone/dropzone";

import { TextField, MenuItem, Stack, IconButton, Tooltip, Autocomplete } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ruRU } from "@mui/x-date-pickers/locales";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import Validator from "@utils/validator";
import clsx from "clsx";
import { BackButton } from "@components/back-button";
import Loader from "@ui/indicators/loader";
import style from "@scss/pages/company/company-edit.module.scss";
import { useQueryClient } from "react-query";
import { companyQueryKeys } from "@lib/company/queryKeys";
import { useSelectedCompanyQuery } from "@lib/company";
import { DepartureScheduleType } from "@interfaces/departure-schedule";
import { getNoun } from "@utils/getNoun";
import { useUnit } from "effector-react";
import moment from "moment";

const CompanyEdit: React.FC<CompanyTabPropsT> = () => {
  const queryClient = useQueryClient();

  const { company, isLoading } = useSelectedCompanyQuery();
  const permissions = useUnit($UserAddPermissions);

  const employees = useUnit($Employees);
  const { isFetched: isEmployeesFetched } = useUnit($EmployeesStates);

  const [validation, setValidation] = useState<any>({});
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const [contacts, setContacts] = useState<CompanyContactT[]>(company?.contacts ?? []);
  const [countDeparture, setCountDeparture] = useState<number>(company?.countDeparture ?? 0);
  const [countDate, setCountDate] = useState<number>(company?.countDate ?? 0);
  const [typeDate, setTypeDate] = useState<DepartureScheduleType>(company?.typeDate !== undefined && company?.typeDate.length > 0 ? company?.typeDate as DepartureScheduleType : DepartureScheduleType.MONTH);
  const history = useNavigate();

  const typeDayToName = [
    {
      key: DepartureScheduleType.DAY,
      title: "День",
    },
    {
      key: DepartureScheduleType.WEEK,
      title: "Неделя",
    },
    {
      key: DepartureScheduleType.MONTH,
      title: "Месяц",
    },
    {
      key: DepartureScheduleType.YEAR,
      title: "Год",
    },
  ];

  useEffect(() => {
    if(company?.contacts){
      setContacts(company?.contacts)
    }
    if(company?.countDeparture){
      setCountDeparture(company?.countDeparture)
    }
    if(company?.countDate){
      setCountDate(company?.countDate)
    }
    if(company?.typeDate && company?.typeDate.length > 0){
      setTypeDate(company?.typeDate as DepartureScheduleType)
    }
  }, [company]);

  useEffect(() => {
    if (!isEmployeesFetched) fetchEmployees();
  }, [isEmployeesFetched]);

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const modalBtn = document.querySelector("button.modal_btn") as HTMLButtonElement;
    modalBtn.disabled = true;
    const contractNumber = document.querySelector('input[name="contractNumber"]') as HTMLInputElement;
    const name = document.querySelector('input[name="name"]') as HTMLInputElement;
    const organizationalLegalForm = document.querySelector('input[name="organizationalLegalForm"]') as HTMLInputElement;
    const inn = document.querySelector('input[name="inn"]') as HTMLInputElement;
    const actualAddress = document.querySelector('input[name="actualAddress"]') as HTMLInputElement;
    const legalAddress = document.querySelector('input[name="legalAddress"]') as HTMLInputElement;
    const shortDesc = document.querySelector('input[name="shortDesc"]') as HTMLInputElement;
    const leadershipPosition = document.querySelector<HTMLInputElement>('input[name="leadershipPosition"]');
    const supervisor = document.querySelector<HTMLInputElement>('input[name="supervisor"]');
    const responsibleUser = document.querySelector<HTMLInputElement>('input[name="responsibleUser"]');
    const startDepartureSchedule = document.querySelector<HTMLInputElement>('input[name="startDepartureSchedule"]');
    const startWorkDate = document.querySelector<HTMLInputElement>('input[name="startWorkDate"]');
    const endWorkDate = document.querySelector<HTMLInputElement>('input[name="endWorkDate"]');
    const accessToken = window.sessionStorage.getItem("accessToken") ?? null;

    const validating = {
      ...Validator(name.value, "name")
        .isLength({ min: 1, max: 1000 })
        .withMessage("Это поле должно содержать не менее 5 символов")
        .getErrors(),
      ...Validator(inn.value, "inn")
        .isRequired()
        .withMessage("Это поле обязательно")
        .getErrors(),
      ...Validator(shortDesc.value, "shortDesc")
        .isLength({ max: 3000 })
        .withMessage("Длина описания не должна превышать 1022 символа")
        .getErrors(),
    };

    if (Validator.hasError(validating)) {
      modalBtn.disabled = false;
      return setValidation(validating);
    }

    const formData = new FormData();
    formData.append("contractNumber", contractNumber?.value);
    formData.append("name", name?.value);
    formData.append("inn", inn?.value);
    formData.append("legalAddress", legalAddress?.value);
    formData.append("actualAddress", actualAddress?.value);
    formData.append("organizationalLegalForm", organizationalLegalForm?.value);
    formData.append("shortDesc", shortDesc?.value);
    formData.append("leadershipPosition", leadershipPosition?.value ?? "");
    formData.append("supervisor", supervisor?.value ?? "");
    formData.append("countDeparture", countDeparture.toString());
    formData.append("typeDate", typeDate);
    formData.append("countDate", countDate.toString());
    formData.append("responsibleUser", responsibleUser?.value ?? "");
    formData.append(
      "startDepartureSchedule",
      startDepartureSchedule?.value ?? ""
    );
    formData.append("contacts", JSON.stringify(contacts));
    formData.append("startWorkDate", startWorkDate?.value && startWorkDate?.value !== 'ДД.ММ.ГГГГ' ? startWorkDate?.value : "");
    formData.append("endWorkDate", endWorkDate?.value && endWorkDate?.value !== 'ДД.ММ.ГГГГ' ? endWorkDate?.value : "");
    if (accessToken && accessToken !== "null") {
      formData.append("accessToken", accessToken);
    }

    if (uploadedFiles.length) {
      formData.append("companyLogo", uploadedFiles[0]);
    }

    if (company?.id) {
      return CompanyService.update(company.id, formData, (err, res) => {
        if (err || !res) {
          if (err?.response?.status === 422) {
            modalBtn.disabled = false;
            setValidation(err.response.data);
          }
          return console.error("При обновлении данных компании произошла ошибка");
        }

        queryClient.invalidateQueries(companyQueryKeys.all);
        history('/company')
      });
    }
  };

  const handleChangeDate = (e: ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case "countDeparture":
        setCountDeparture(+e.target.value);
        break;
      case "countDate":
        setCountDate(+e.target.value);
        break;
      case "typeDate":
        setTypeDate(e.target.value as DepartureScheduleType);
        break;
    }
  };

  const renderNounType = () => {
    switch (typeDate) {
      case DepartureScheduleType.DAY:
        return getNoun(countDate, "день", "дня", "дней");
      case DepartureScheduleType.WEEK:
        return getNoun(countDate, "неделю", "недели", "недель");
      case DepartureScheduleType.MONTH:
        return getNoun(countDate, "месяц", "месяца", "месяцев");
      case DepartureScheduleType.YEAR:
        return getNoun(countDate, "год", "года", "лет");
      default:
        return getNoun(countDate, "месяц", "месяца", "месяцев");
    }
  };

  const addContacts = (e: any) => {
    if(contacts.length < 5){
      contacts.push({} as CompanyContactT);
      return setContacts([...contacts]);
    }
    return;
  };

  const removeContacts = (index: number) => {
    contacts.splice(index, 1);
    return setContacts([...contacts]);
  };

  const changeContacts = (index: number, type: 'name' | 'phone' | 'email', value: string) => {
    contacts[index][type] = value;
    return setContacts([...contacts]);
  };

  const handleClearDepartureSchedule = () => {
    setCountDeparture(0);
    setCountDate(0);
    setTypeDate(DepartureScheduleType.DAY);
  }

  if (!company || isLoading) {
    return <Loader />;
  }

  return (
    <section className={`tab-content-item ${clsx(style.edit_company_container)}`}>
      <BackButton link={"/company/companies"} />
      <div className={clsx(style.tab_top_content)}>
        <Stack direction="row" spacing={2} mt={3} mb={2} alignItems="center">
          <img alt={company.name} src={getCompanyLogo(company.image)} className={clsx(style.company_img)} />
          <h5 className={clsx(style.company_name)}>{company.name}</h5>
        </Stack>
        <form onSubmit={(e) => handleSubmit(e)}>
          <TextField
            fullWidth
            name="contractNumber"
            label="№ Договора"
            variant="filled"
            required
            defaultValue={company.contractNumber || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <Stack direction="row" spacing={2} mt={2}>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale={"ru"}
              localeText={
                ruRU.components.MuiLocalizationProvider.defaultProps.localeText
              }
            >
              <DatePicker
                value={company.startWorkDate ? moment(company.startWorkDate, 'DD.MM.YYYY') : null}
                label="Дата начала действия договора"
                name="startWorkDate"
                format="DD.MM.YYYY"
                autoFocus={false}
                sx={{ width: 1 }}
                slotProps={{
                  actionBar: { actions: ["cancel", "accept"] },
                  textField: {
                    variant: "filled",
                  },
                }}
                localeText={{
                  toolbarTitle: "",
                  cancelButtonLabel: "Отменить",
                  okButtonLabel: "ОК",
                }}
              />
              <DatePicker
                value={company.endWorkDate ? moment(company.endWorkDate, 'DD.MM.YYYY') : null}
                label="Дата окончания действия договора"
                name="endWorkDate"
                format="DD.MM.YYYY"
                autoFocus={false}
                sx={{ width: 1 }}
                slotProps={{
                  actionBar: { actions: ["cancel", "accept"] },
                  textField: {
                    variant: "filled",
                  },
                }}
                localeText={{
                  toolbarTitle: "",
                  cancelButtonLabel: "Отменить",
                  okButtonLabel: "ОК",
                }}
              />
            </LocalizationProvider>
          </Stack>
          <TextField
            fullWidth
            name="name"
            label="Наименование организации"
            variant="filled"
            required
            defaultValue={company.name || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.nameError}</p>
          <TextField
            fullWidth
            name="organizationalLegalForm"
            label="Организационно-правовая форма"
            variant="filled"
            defaultValue={company.organizationalLegalForm || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.organizationalLegalFormError}</p>
          <TextField
            fullWidth
            name="inn"
            label="ИНН"
            variant="filled"
            required
            defaultValue={company.inn || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.innError}</p>
          <TextField
            fullWidth
            name="actualAddress"
            label="Адрес фактический"
            variant="filled"
            defaultValue={company.actualAddress || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.actualAddressError}</p>
          <TextField
            fullWidth
            name="legalAddress"
            label="Адрес юридический"
            variant="filled"
            required
            defaultValue={company.legalAddress || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.legalAddressError}</p>
          <TextField
            fullWidth
            name="shortDesc"
            label="Краткое описание"
            variant="filled"
            defaultValue={company.shortDesc || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.shortDescError}</p>
          <TextField
            fullWidth
            name="leadershipPosition"
            label="Должность руководителя"
            variant="filled"
            required
            defaultValue={company.leadershipPosition || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.leadershipPositionError}</p>
          <TextField
            fullWidth
            name="supervisor"
            label="Руководитель"
            variant="filled"
            required
            defaultValue={company.supervisor || ""}
            slotProps={{
              input: {
                autoComplete: "off",
              },
            }}
            sx={{ mt: 2 }}
          />
          <p className="error-text">{validation.supervisorError}</p>

          { company?.users &&
            company?.users.length > 0 &&
            permissions.hasPermission(
              ACTIONS.companyDepartureSchedule_allowedToCreate
            ) && (
              <div className={clsx(style.departureSchedule)}>
                <p>Период выездов <button type="button" className={clsx(style.btn_small)} onClick={handleClearDepartureSchedule}>очистить</button></p>
                <Stack direction="row" spacing={2} justifyContent="space-between" mt={1}>
                  <TextField
                    fullWidth
                    label="Кол-во выездов"
                    placeholder="Введите значение"
                    variant="filled"
                    name="countDeparture"
                    type="number"
                    value={countDeparture}
                    onChange={handleChangeDate}
                  />
                  <TextField
                    select
                    fullWidth
                    variant="filled"
                    label="Период"
                    defaultValue={typeDate}
                    name="typeDate"
                    onChange={handleChangeDate}
                  >
                    {typeDayToName.map((item) => (
                      <MenuItem value={item.key} key={item.key} disabled={item.key !== DepartureScheduleType.MONTH}>
                        {item.title}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Кол-во периодов"
                    placeholder="Введите значение"
                    variant="filled"
                    name="countDate"
                    type="number"
                    value={countDate}
                    onChange={handleChangeDate}
                  />
                </Stack>
                <p style={{ fontSize: 16, marginTop: 5 }}>
                {`
                  ${countDeparture} 
                  ${getNoun(countDeparture, "выезд", "выезда", "выездов")} 
                  ${getNoun(countDate, "в", "за", "за")}
                  ${countDate} 
                  ${renderNounType()}
                `}
                </p>
                <Stack direction="row" spacing={2} justifyContent="space-between" mt={2}>
                  {company?.users && (
                    <TextField
                      select
                      fullWidth
                      variant="filled"
                      label="Ответственный за выезды"
                      defaultValue={company?.responsibleUser}
                      name="responsibleUser"
                    >
                      {company?.users.map((item) => (
                        <MenuItem value={item.id} key={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={"ru"}
                    localeText={
                      ruRU.components.MuiLocalizationProvider.defaultProps
                        .localeText
                    }
                  >
                    <DatePicker
                      value={company.startDepartureSchedule ? moment(company.startDepartureSchedule) : null}
                      name="startDepartureSchedule"
                      format="DD.MM.YYYY"
                      autoFocus={false}
                      slotProps={{
                        actionBar: { actions: ["clear", "cancel", "accept"] },
                        textField: {
                          variant: "filled",
                          label: "Начало выездов",
                        },
                      }}
                      localeText={{
                        toolbarTitle: "",
                        cancelButtonLabel: "Отменить",
                        okButtonLabel: "ОК",
                        clearButtonLabel: "Очистить",
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </div>
          )}
          { company?.users.length === 0 &&
            permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToCreate) && (
            <p className={clsx(clsx(style.departureSchedule_info))}>Для выбора Периодов выезда нужно добавить хотя бы одного ответственного сотрудника</p>
          )}
          <div className={clsx(style.contacts)}>
            <p>
              Контактные лица:
              <Tooltip title="Добавить" placement="top" arrow>
                <IconButton
                  aria-label="fingerprint"
                  color="success"
                  onClick={addContacts}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </p>
            {contacts && contacts.map((e, i) => (
              <Stack direction="row" spacing={2} mb={2} alignItems="center">
                <Stack direction="column" spacing={2} width={1} sx={i !== contacts.length - 1 ? {  borderBottom: '1px dashed grey' } : {}} pb={1}>
                  <Autocomplete
                    fullWidth
                    freeSolo
                    size="small"
                    options={employees ? employees.sort((a, b) => (a.position.department.id - b.position.department.id)) : []}
                    getOptionLabel={(option) => (typeof option !== 'string' ? option.name : option)}
                    getOptionKey={(option) => (typeof option !== 'string' ? option.id : option)}
                    groupBy={(option) => option.position.department.title}
                    inputValue={e.name}
                    onChange={(e, value) => {
                      if (value){
                        changeContacts(i, 'name', typeof value !== 'string' ? value.name : value);
                        if(typeof value !== 'string'){
                          changeContacts(i, 'phone', value.phone);
                          changeContacts(i, 'email', value.email);
                        }
                      }
                    }}
                    onInputChange={(e, value) => changeContacts(i, 'name', value)}
                    slotProps={{
                      listbox: {
                        sx: {
                          padding: "0",
                        },
                      },
                      popper: {
                        popperOptions: {
                          placement: "bottom-start",
                        },
                        sx: {
                          width: "auto !important",
                          maxWidth: "30vw",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        label="ФИО"
                        placeholder="Введите значение"
                      />
                    )}
                  />
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      label="Телефон"
                      placeholder="Введите значение"
                      variant="filled"
                      size="small"
                      value={e.phone}
                      onChange={(e) => changeContacts(i, 'phone', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      placeholder="Введите значение"
                      variant="filled"
                      size="small"
                      value={e.email}
                      onChange={(e) => changeContacts(i, 'email', e.target.value)}
                    />
                  </Stack>
                </Stack>
                <Tooltip title="Удалить" placement="top" arrow>
                  <IconButton
                    aria-label="fingerprint"
                    color="success"
                    sx={{ maxHeight: '40px' }}
                    onClick={() => removeContacts(i)}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </div>
          <Dropzone
            maxFiles={1}
            onUpload={(files) => setUploadedFiles(files)}
            accept="image/jpeg, image/png"
            type={"company"}
          />
          <p className="error-text">{validation.imageError}</p>
          <button type="submit" className="modal_btn">
            Сохранить
          </button>
        </form>
      </div>
    </section>
  );
};

export default CompanyEdit;
